import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  BookOpen,
  Users,
  Link as LinkIcon,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  Trash2,
} from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import { ScrollArea } from "../../ui/scroll-area";
import { db } from "../../../lib/db";
import { toast } from "sonner";
import { formatDate } from "../../../lib/dateUtils";
import { useAuthStore } from "../../../store/authStore";
import { v4 as uuidv4 } from "uuid";
import { useConfirmDialog } from "../../shared/ConfirmDialog";

export default function CoursesPage() {
  const { user } = useAuthStore();
  const { confirm } = useConfirmDialog();
  const [courses, setCourses] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);

  // Form Data
  const [newCourse, setNewCourse] = useState({
    title: "",
    link: "",
    description: "",
  });
  const [assignData, setAssignData] = useState({ courseId: "", dueDate: "" });
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [coursesData, allocationsData, usersData] = await Promise.all([
        db.courses.toArray(),
        db.courseAllocations.toArray(),
        db.users.toArray(),
      ]);
      setCourses(coursesData.sort((a, b) => b.createdAt - a.createdAt));
      setAllocations(allocationsData);
      setUsers(usersData);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async () => {
    if (!newCourse.title || !newCourse.link) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const courseId = uuidv4();
      await db.courses.add({
        id: courseId,
        ...newCourse,
        createdBy: user.id,
        createdAt: Date.now(),
      });
      toast.success("Course created successfully");
      setShowCreateModal(false);
      setNewCourse({ title: "", link: "", description: "" });
      loadData();
    } catch (error) {
      toast.error("Failed to create course");
    }
  };

  const handleAssignCourse = async () => {
    if (
      !assignData.courseId ||
      !assignData.dueDate ||
      selectedEmployees.length === 0
    ) {
      toast.error("Please select course, due date, and employees");
      return;
    }

    try {
      const allocations = selectedEmployees.map((userId) => ({
        id: uuidv4(),
        courseId: assignData.courseId,
        userId,
        assignedBy: user.id,
        status: "pending",
        dueDate: new Date(assignData.dueDate).getTime(),
        assignedAt: Date.now(),
        updatedAt: Date.now(),
      }));

      await db.courseAllocations.bulkAdd(allocations);
      toast.success(`Course assigned to ${selectedEmployees.length} employees`);
      setShowAssignModal(false);
      setSelectedEmployees([]);
      setAssignData({ courseId: "", dueDate: "" });
      loadData();
    } catch (error) {
      toast.error("Failed to assign course");
    }
  };

  const handleDeleteCourse = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Course',
      description: 'Are you sure? This will not delete existing allocations.',
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (confirmed) {
      await db.courses.delete(id);
      toast.success("Course deleted");
      loadData();
    }
  };

  const employees = users.filter((u) => u.role !== "admin");
  const filteredEmployees = employees.filter(
    (emp) =>
      emp.profile?.firstName
        ?.toLowerCase()
        .includes(employeeSearch.toLowerCase()) ||
      emp.profile?.lastName
        ?.toLowerCase()
        .includes(employeeSearch.toLowerCase())
  );

  const toggleEmployee = (id) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  const selectAllEmployees = () => {
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map((e) => e.id));
    }
  };

  const getCourseStats = (courseId) => {
    const courseAllocations = allocations.filter(
      (a) => a.courseId === courseId
    );
    return {
      total: courseAllocations.length,
      completed: courseAllocations.filter((a) => a.status === "completed")
        .length,
      inProgress: courseAllocations.filter((a) => a.status === "started")
        .length,
      pending: courseAllocations.filter((a) => a.status === "pending").length,
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Learning Management
          </h1>
          <p className="text-gray-500 mt-1">
            Create courses and track employee progress
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAssignModal(true)} variant="outline">
            <Users className="w-4 h-4 mr-2" /> Assign Course
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Course
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Total Courses
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {courses.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Active Learners
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {
                    new Set(
                      allocations
                        .filter((a) => a.status === "started")
                        .map((a) => a.userId)
                    ).size
                  }
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Completions</p>
                <p className="text-3xl font-bold text-gray-900">
                  {allocations.filter((a) => a.status === "completed").length}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-4">
            {courses
              .filter((c) =>
                c.title.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((course) => {
                const stats = getCourseStats(course.id);
                return (
                  <div
                    key={course.id}
                    className="flex flex-col md:flex-row md:items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">
                          {course.title}
                        </h3>
                        <a
                          href={course.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-600"
                        >
                          <LinkIcon className="w-4 h-4" />
                        </a>
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-2">
                        {course.description}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />{" "}
                          {formatDate(course.createdAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" /> {stats.total} Assigned
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="flex gap-4 text-center">
                        <div>
                          <p className="text-xs text-gray-500">Pending</p>
                          <p className="font-semibold text-gray-700">
                            {stats.pending}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Started</p>
                          <p className="font-semibold text-amber-600">
                            {stats.inProgress}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Done</p>
                          <p className="font-semibold text-emerald-600">
                            {stats.completed}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteCourse(course.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            {courses.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No courses found. Create one to get started.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Course Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Course</DialogTitle>
            <DialogDescription>
              Add a new learning resource for your employees.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Course Title</Label>
              <Input
                placeholder="e.g., Advanced React Patterns"
                value={newCourse.title}
                onChange={(e) =>
                  setNewCourse({ ...newCourse, title: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Course Link</Label>
              <Input
                placeholder="https://..."
                value={newCourse.link}
                onChange={(e) =>
                  setNewCourse({ ...newCourse, link: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Brief description of the course content..."
                value={newCourse.description}
                onChange={(e) =>
                  setNewCourse({ ...newCourse, description: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCourse}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              Create Course
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Course Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Course</DialogTitle>
            <DialogDescription>
              Select a course and assign it to employees.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Select Course</Label>
                <Select
                  value={assignData.courseId}
                  onValueChange={(v) =>
                    setAssignData({ ...assignData, courseId: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a course..." />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={assignData.dueDate}
                  onChange={(e) =>
                    setAssignData({ ...assignData, dueDate: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Select Employees</Label>
              <div className="border rounded-md p-4">
                <Input
                  placeholder="Search employees..."
                  className="mb-3"
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                />
                <div className="flex items-center space-x-2 mb-2 pb-2 border-b">
                  <Checkbox
                    id="select-all"
                    checked={
                      selectedEmployees.length === filteredEmployees.length &&
                      filteredEmployees.length > 0
                    }
                    onCheckedChange={selectAllEmployees}
                  />
                  <label
                    htmlFor="select-all"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Select All
                  </label>
                </div>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {filteredEmployees.map((emp) => (
                      <div
                        key={emp.id}
                        className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded"
                      >
                        <Checkbox
                          id={`emp-${emp.id}`}
                          checked={selectedEmployees.includes(emp.id)}
                          onCheckedChange={() => toggleEmployee(emp.id)}
                        />
                        <label
                          htmlFor={`emp-${emp.id}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {emp.profile?.firstName} {emp.profile?.lastName}
                          <span className="text-gray-400 text-xs ml-2">
                            ({emp.profile?.department})
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
              <p className="text-sm text-gray-500 text-right">
                {selectedEmployees.length} employee(s) selected
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignCourse}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              Assign Course
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
